/**
 * Shared table utilities — exact port of
 * `manuals/COP-microservice-integration/table-utils.jsx` (the operator's
 * canonical reference). DO NOT diverge — when the reference updates,
 * mirror those changes here so every COP page behaves identically.
 *
 * Used by all COP ADMS pages (Datasets / CoT / NFFI / ADatP-3 Bridge /
 * ADatP-3 Orders / Locations / Intelligence).
 *
 * Three concerns, several small APIs:
 *   1. CSV / JSON export        — exportRowsAsCsv() / exportRowsAsJson()
 *   2. Sort + pagination state  — useTableState() hook + <Pagination/>
 *   3. Last-refresh timestamp   — <LastRefresh/>
 *   4. TTL warning indicator    — <TtlBadge/>  (fresh / ageing / stale / expiring)
 *   5. Search input             — <SearchInput/>
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

// ════════════════════════════════════════════════════════════════════════
//  1. CSV / JSON Export
// ════════════════════════════════════════════════════════════════════════

export function rowsToCsv(rows, columns) {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  const cols = columns || Object.keys(rows[0]).map((k) => ({ key: k, label: k }));
  const escape = (v) => {
    if (v == null) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = cols.map((c) => escape(c.label || c.key)).join(',');
  const body = rows
    .map((r) => cols.map((c) => escape(c.get ? c.get(r) : r[c.key])).join(','))
    .join('\r\n');
  return header + '\r\n' + body;
}

export function triggerDownload(content, filename, mimeType = 'text/plain') {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType + ';charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportRowsAsCsv(rows, columns, filename) {
  triggerDownload(rowsToCsv(rows, columns), filename, 'text/csv');
}

export function exportRowsAsJson(rows, filename) {
  triggerDownload(JSON.stringify(rows, null, 2), filename, 'application/json');
}

export function ExportButtons({ getRows, columns, filenameBase, disabled = false, small = false }) {
  const btnCls = small
    ? 'rounded-lg border border-line bg-white px-2.5 py-1 text-[11px] font-medium text-mist hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed'
    : 'rounded-xl border border-line bg-white px-3 py-1.5 text-xs font-medium text-mist hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed';
  const stamp = `${filenameBase}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}`;
  const handle = (kind) => {
    if (disabled) return;
    const rows = getRows?.() || [];
    if (rows.length === 0) return;
    if (kind === 'csv') exportRowsAsCsv(rows, columns, `${stamp}.csv`);
    else exportRowsAsJson(rows, `${stamp}.json`);
  };
  return (
    <div className="inline-flex gap-1.5" role="group" aria-label="Export">
      <button type="button" className={btnCls} disabled={disabled} onClick={() => handle('csv')} title="Export visible rows as CSV">
        <i className="fa-sharp fa-solid fa-file-csv mr-1" />CSV
      </button>
      <button type="button" className={btnCls} disabled={disabled} onClick={() => handle('json')} title="Export visible rows as JSON">
        <i className="fa-sharp fa-solid fa-file-code mr-1" />JSON
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  2. Sort + Pagination
// ════════════════════════════════════════════════════════════════════════

export function useTableState(rows, {
  defaultSortKey = null,
  defaultSortDir = 'desc',
  pageSize = 25,
  sortAccessors = {},
} = {}) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [page, setPage] = useState(0);

  // Reset to page 0 when row count changes drastically (avoids empty page).
  useEffect(() => { setPage(0); }, [rows.length]);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const acc = sortAccessors[sortKey] || ((r) => r[sortKey]);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = acc(a), bv = acc(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [rows, sortKey, sortDir, sortAccessors]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageSafe = Math.min(page, totalPages - 1);
  const pageRows = useMemo(
    () => sorted.slice(pageSafe * pageSize, (pageSafe + 1) * pageSize),
    [sorted, pageSafe, pageSize]
  );

  const toggleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(0);
  }, [sortKey]);

  return {
    pageRows,
    sortedRows: sorted,
    sortKey, sortDir, toggleSort,
    page: pageSafe, totalPages, pageSize,
    setPage,
    total: sorted.length,
  };
}

export function SortIcon({ active, dir }) {
  if (!active) return <i className="fa-sharp fa-regular fa-sort ml-1 text-[10px] text-slate-300" />;
  return (
    <i className={`fa-sharp fa-solid ml-1 text-[10px] text-slate-500 ${dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`} />
  );
}

export function Pagination({ page, totalPages, total, pageSize, setPage }) {
  if (totalPages <= 1) return null;
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line bg-slate-50/50 px-4 py-2 text-xs text-mist">
      <div className="tabular-nums">
        Showing <span className="font-semibold text-ink">{from}–{to}</span> of <span className="font-semibold text-ink">{total}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="rounded-lg border border-line bg-white px-2 py-1 text-[11px] hover:bg-slate-50 disabled:opacity-40"
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
        >
          <i className="fa-sharp fa-solid fa-chevron-left" /> Prev
        </button>
        <span className="px-2 tabular-nums">
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          className="rounded-lg border border-line bg-white px-2 py-1 text-[11px] hover:bg-slate-50 disabled:opacity-40"
          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
        >
          Next <i className="fa-sharp fa-solid fa-chevron-right" />
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  3. Last-refresh
// ════════════════════════════════════════════════════════════════════════

export function LastRefresh({ ts, prefix = 'Refreshed' }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!ts) {
    return <span className="text-[11px] text-mist tabular-nums">{prefix}: —</span>;
  }
  const t = ts instanceof Date ? ts : new Date(ts);
  const sec = Math.max(0, Math.floor((now - t.getTime()) / 1000));
  let age;
  if (sec < 5)        age = 'just now';
  else if (sec < 60)  age = `${sec}s ago`;
  else if (sec < 3600) age = `${Math.floor(sec / 60)}m ago`;
  else                age = `${Math.floor(sec / 3600)}h ago`;
  const hh = String(t.getHours()).padStart(2, '0');
  const mm = String(t.getMinutes()).padStart(2, '0');
  const ss = String(t.getSeconds()).padStart(2, '0');
  return (
    <span className="text-[11px] text-mist tabular-nums" title={t.toISOString()}>
      <i className="fa-sharp fa-regular fa-clock mr-1" />
      {prefix} {hh}:{mm}:{ss} <span className="text-slate-400">({age})</span>
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  4. TTL badge — "fresh / ageing / stale / expiring · <age>"
// ════════════════════════════════════════════════════════════════════════

export function TtlBadge({ lastUpdate, ttlMs }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  if (!lastUpdate) {
    return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-mist">no data</span>;
  }

  const ts = lastUpdate instanceof Date ? lastUpdate : new Date(lastUpdate);
  const ageMs = Math.max(0, now - ts.getTime());
  const ageMin = Math.floor(ageMs / 60000);
  const ageStr = ageMin < 60
    ? `${ageMin}m ago`
    : ageMin < 1440
      ? `${Math.floor(ageMin / 60)}h ${ageMin % 60}m ago`
      : `${Math.floor(ageMin / 1440)}d ago`;

  if (!ttlMs || ttlMs <= 0) {
    return (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-mist tabular-nums" title={ts.toISOString()}>
        {ageStr}
      </span>
    );
  }

  const pct = ageMs / ttlMs;
  let cls, label;
  if (pct < 0.5)      { cls = 'bg-emerald-50 text-emerald-700 border-emerald-200'; label = 'fresh'; }
  else if (pct < 0.75){ cls = 'bg-amber-50 text-amber-700 border-amber-200';       label = 'ageing'; }
  else if (pct < 0.9) { cls = 'bg-orange-50 text-orange-700 border-orange-200';    label = 'stale'; }
  else                { cls = 'bg-rose-50 text-rose-700 border-rose-200';          label = 'expiring'; }
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium tabular-nums ${cls}`}
      title={`${ts.toISOString()} — ${Math.round(pct * 100)}% of TTL elapsed`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
      <span>{label}</span>
      <span className="text-slate-400">·</span>
      <span>{ageStr}</span>
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  5. Search input (debounced)
// ════════════════════════════════════════════════════════════════════════

export function SearchInput({ value, onChange, placeholder = 'Search…', debounceMs = 200, className = '', small = false }) {
  const [local, setLocal] = useState(value || '');
  useEffect(() => { setLocal(value || ''); }, [value]);
  useEffect(() => {
    if (local === value) return;
    const t = setTimeout(() => onChange?.(local), debounceMs);
    return () => clearTimeout(t);
  }, [local, value, onChange, debounceMs]);

  const inputCls = small
    ? 'field w-full !py-1 !pl-7 !text-xs'
    : 'field w-full !py-1.5 !pl-8 !text-sm';
  const iconCls = small
    ? 'fa-sharp fa-solid fa-magnifying-glass absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400'
    : 'fa-sharp fa-solid fa-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400';

  return (
    <div className={`relative ${className}`}>
      <i className={iconCls} />
      <input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
        aria-label="Search"
      />
      {local && (
        <button
          type="button"
          onClick={() => { setLocal(''); onChange?.(''); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 hover:text-ink"
          title="Clear"
        >
          <i className="fa-sharp fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );
}

export function applySearch(rows, query, accessors) {
  if (!query || !query.trim()) return rows;
  const tokens = query.trim().toLowerCase().split(/\s+/);
  return rows.filter((row) => {
    const blob = accessors
      .map((a) => {
        try {
          const v = typeof a === 'function' ? a(row) : row[a];
          return v == null ? '' : String(v);
        } catch { return ''; }
      })
      .join(' ')
      .toLowerCase();
    return tokens.every((t) => blob.includes(t));
  });
}
